// ui/useHandLifecycle.js: M2.c-10d hand/game lifecycle driver.
//
// Watches the reducer snapshot and advances the game between hands:
//   end_of_hand (post-pass-out or after score_hand fires) ── if game
//   isn't over ── dispatches RESET_GAME-equivalent next_hand command:
//   rotates the dealer clockwise + deals with the next seed from the
//   deterministic stream.
//
// Why the seed stream is deterministic (not Date.now() per hand): a
// player who hits a weird Euchre bug can attach the first-game seed
// to the report, and we can replay every hand of that game by
// re-feeding the same seed. Per codex review #14 + plan AC for 10d.
//
// First-game seed is taken from Date.now() once at game start. After
// that, each next-hand seed is derived from the current state with
// a fast PRNG (xorshift32, adequate for deal randomness, not for
// security).
//
// The lifecycle hook also auto-runs `SCORE` once after trick #5 so
// the EndOfHandModal has score_summary populated. After modal
// dismissal we dispatch next_hand. Implementation: when snapshot
// transitions to `current_state === 'score'` we dispatch SCORE;
// when it transitions to `end_of_hand && !score_summary` we no-op
// (waiting on prior frame to land). When `score_summary` exists +
// `!game_over` + the user clicks Continue (or we auto-continue),
// dispatch next_hand.

import { useEffect, useRef } from 'preact/hooks';
import { NEXT_HAND_ACTION_TYPE } from './gameReducer.js';

/**
 * Deterministic seed stream. Seed -> stream of 32-bit ints. Calling
 * .next() advances; .peek() reads without advancing. Pure JS so a
 * bug report's first-game seed can replay the whole game.
 *
 * Uses xorshift32, well-distributed for non-crypto uses.
 *
 * @param {number} initialSeed
 * @returns {{ next: () => number, peek: () => number, current: () => number }}
 */
export function makeSeedStream(initialSeed) {
  let s = (initialSeed | 0) || 1;
  function step() {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return s | 0;
  }
  return {
    next() {
      const v = step();
      return Math.abs(v) % 0x7fffffff;
    },
    peek() {
      // Snapshot the state, step, restore.
      const save = s;
      const v = step();
      s = save;
      return Math.abs(v) % 0x7fffffff;
    },
    current() { return s; },
  };
}

/**
 * Rotate dealer clockwise: 1 → 2 → 3 → 4 → 1. Pure.
 * @param {1|2|3|4} seat
 * @returns {1|2|3|4}
 */
export function nextDealerSeat(seat) {
  const n = (seat % 4) + 1;
  return /** @type {1|2|3|4} */ (n);
}

/**
 * @param {{
 *   snapshot: object|null,
 *   dispatch: (a: any) => void,
 *   actionCreators: any,
 *   luaGameState: import('../lua/game/lua_bridge.js').LuaGameState|null,
 *   seedStream: { current: { next: () => number } | null },
 *   onHandEnded?: (snap: object) => void,
 * }} opts
 */
export function useHandLifecycle(opts) {
  const { snapshot, dispatch, actionCreators, luaGameState, seedStream, onHandEnded } = opts;
  // Track which (hand_index, phase) we've already responded to so we
  // don't double-dispatch when React re-renders without a real state
  // change.
  const lastHandledRef = useRef(/** @type {string|null} */ (null));

  useEffect(() => {
    if (!snapshot || !luaGameState) return;
    const handIx = snapshot.hand_index ?? 0;

    // After the 5th trick, state.lua sets current_state='score' AND
    // runs score_hand internally, score_summary is already populated
    // by the time the snapshot lands. The EndOfHandModal opens on this
    // state. No SCORE dispatch needed: state.lua already ran it.
    //
    // The 'end_of_hand' literal state is reachable only in the
    // round-2 pass-out (stick_the_dealer=false) path; we still treat
    // it as a modal-opening state for completeness.
    if ((snapshot.current_state === 'score'
         || snapshot.current_state === 'end_of_hand')
        && snapshot.score_summary
        && !snapshot.game_over) {
      const key = `${handIx}|hand_done`;
      if (lastHandledRef.current !== key) {
        lastHandledRef.current = key;
        onHandEnded?.(snapshot);
      }
    }
  }, [snapshot, luaGameState, dispatch, actionCreators, onHandEnded]);

  // Provide an imperative API for the Continue button: dispatch
  // NEXT_HAND with the next dealer + next seed. Stable across
  // renders.
  const continueRef = useRef(() => {});
  continueRef.current = () => {
    if (!snapshot || !luaGameState || !seedStream.current) return;
    if (snapshot.game_over) return;
    const dealerNext = nextDealerSeat(snapshot.dealer_seat);
    const seed = seedStream.current.next();
    dispatch({ type: NEXT_HAND_ACTION_TYPE, nextDealerSeat: dealerNext, nextSeed: seed });
  };

  return { continueHand: () => continueRef.current() };
}
